"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";

const LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
];

interface LanguageSwitcherProps {
  currentLocale?: string;
  compact?: boolean;
}

export function LanguageSwitcher({ currentLocale = "en", compact = false }: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  const current = LANGUAGES.find((l) => l.code === currentLocale) || LANGUAGES[0];

  async function handleLocaleChange(locale: string) {
    setIsOpen(false);
    await fetch("/api/locale", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ locale }),
    });
    startTransition(() => {
      router.refresh();
    });
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 rounded-xl transition-colors",
          compact
            ? "px-2 py-1.5 text-xs hover:bg-white/5"
            : "px-3 py-2 text-sm border border-surface-border hover:bg-white/5",
          isPending && "opacity-50",
        )}
      >
        <span>{current.flag}</span>
        {!compact && <span className="text-text-muted">{current.name}</span>}
        <svg className="h-3 w-3 text-text-dim" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] rounded-xl border border-surface-border bg-surface p-1 shadow-xl">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLocaleChange(lang.code)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors",
                  lang.code === currentLocale
                    ? "bg-white/8 text-white"
                    : "text-text-muted hover:bg-white/5 hover:text-white",
                )}
              >
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
